import { Star } from '@mui/icons-material'
import { Grid, Paper, Tooltip } from '@mui/material'
import { observer } from 'mobx-react-lite'
import React, { useCallback, useEffect, useMemo } from 'react'
import { getI18n, useTranslation } from 'react-i18next'
import { getLocalizedRoleName } from '../../../common/roleHelper'
import history from '../../../history'
import { IUserRole } from '../../../interfaces/roles/IUserRoles'
import { useStore } from '../../../Provider'
import { OPLoadingCircle } from '../../OPcomponents/OPLoadingcircle'
import { Data, OPNewTable } from '../../OPcomponents/OPNewTable'
import OpenRole from './OpenRole'

const RoleTable = (): JSX.Element => {
  const { t } = useTranslation()
  const rootStore = useStore()

  const { validatedUser } = rootStore.loginStore
  const { filteredRoles, loadingSkills, getAllUserRoles } = rootStore.personStore

  const profileClickedId = history.location.state as string

  useEffect(() => {
    getAllUserRoles(validatedUser)
  }, [getAllUserRoles, validatedUser])

  const openRoleRow = useCallback(
    async (id: number | string) => {
      const role = filteredRoles.find((r) => r.roleId === Number(id))
      if (role) {
        return {
          role,
          projects: role.projectNames,
          linkedProjects: role.linkedProjects || [],
        }
      }
    },
    [filteredRoles],
  )

  const onRowClose = useCallback(() => {
    // No additional action needed as filtered work histories are computed properties
  }, [])

  const rows: Data[] = useMemo(() => {
    const lang = getI18n().language

    return filteredRoles.map((role: IUserRole) => {
      const displayName = getLocalizedRoleName(role)
      const categoryName = lang === 'en' && role.categoryEng ? role.categoryEng : role.categoryFi

      return {
        id: role.roleId,
        rows: {
          [t('Roles.RoleName')]: {
            text: displayName,
            width: '38%',
          },
          [t('Roles.Category')]: {
            text: categoryName || t('Projects.NotDefined'),
            width: '38%',
          },
          [t('Roles.KeyRole')]: {
            text: role.isKeyRole ? <Star color='primary' /> : '',
            width: '8%',
            align: 'center' as const,
          },
          [t('Skills.Expirience')]: {
            text: `${role.experienceDays || 0} ${t('Date.ExperienceDays')}`,
            width: '8%',
            align: 'right' as const,
          },
          [t('Roles.Projects')]: {
            text: (
              <Tooltip title={role.projectNames?.join(', ')} placement='bottom' arrow>
                <span>{role.projectNames?.length || 0}</span>
              </Tooltip>
            ),
            width: '8%',
            align: 'right' as const,
          },
        },
      } as Data
    })
  }, [filteredRoles, t])

  return (
    <>
      {loadingSkills ? (
        <Paper className='profile--paper__loadingskills'>
          <OPLoadingCircle />
        </Paper>
      ) : rows.length === 0 ? (
        <Paper className='profile--paper__addtitlepadding'>
          <Grid container textAlign='center'>
            <Grid item xs={12}>
              <p className='profile--deptext'>{t('Roles.RolesPlaceholder')}</p>
            </Grid>
          </Grid>
        </Paper>
      ) : (
        <OPNewTable
          profileclickedId={profileClickedId}
          rows={rows}
          curLength={rows.length}
          onRowClose={onRowClose}
          FormBase={OpenRole}
          onRowClick={openRoleRow}
        />
      )}
    </>
  )
}

export default observer(RoleTable)
